import React, { useRef } from 'react';
import { MoodId } from '../moods';
import './AnimatedMoodIllustration.css';

// A process-wide counter gives each instance a stable, unique id for its SVG
// defs (clipPaths), so two scenes of the same mood never collide on one page.
let instanceSeq = 0;

/**
 * AnimatedMoodIllustration — an original, layered SVG scene per mood.
 *
 * Each mood has its own visual language (sun, waves, target, cloud, breathing
 * rings, moon) built from inline SVG + CSS keyframes — no image assets, no emoji
 * as the only representation. Elements animate at different, gentle timings; all
 * motion stops under prefers-reduced-motion (see the stylesheet). The scene takes
 * its primary colour from the mood accent via `color`, so it themes automatically.
 */
export type IllustrationSize = 'sm' | 'md' | 'lg';

const SIZE_PX: Record<IllustrationSize, number> = { sm: 46, md: 128, lg: 184 };

const Scene: React.FC<{ mood: MoodId; uid: string }> = ({ mood, uid }) => {
  switch (mood) {
    case 'happy':
      return (
        <svg viewBox="0 0 120 120" className="mm-illus__svg">
          <g className="mm-illus-rays" style={{ transformOrigin: '60px 60px' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <rect key={i} x="58.5" y="8" width="3" height="14" rx="1.5" fill="currentColor" opacity="0.55"
                transform={`rotate(${i * 45} 60 60)`} />
            ))}
          </g>
          <circle className="mm-illus-sun" cx="60" cy="60" r="26" fill="currentColor" style={{ transformOrigin: '60px 60px' }} />
          <circle cx="60" cy="60" r="26" fill="none" stroke="#fff" strokeOpacity="0.5" strokeWidth="2" />
          {[[36, 34], [90, 44], [82, 86]].map(([x, y], i) => (
            <g key={i} className="mm-illus-sparkle" style={{ transformOrigin: `${x}px ${y}px`, animationDelay: `${i * 0.6}s` }}>
              <path d={`M${x} ${y - 6} L${x + 1.6} ${y - 1.6} L${x + 6} ${y} L${x + 1.6} ${y + 1.6} L${x} ${y + 6} L${x - 1.6} ${y + 1.6} L${x - 6} ${y} L${x - 1.6} ${y - 1.6} Z`}
                fill="currentColor" opacity="0.8" />
            </g>
          ))}
        </svg>
      );
    case 'calm':
      return (
        <svg viewBox="0 0 120 120" className="mm-illus__svg">
          <clipPath id={`mm-calm-clip-${uid}`}><circle cx="60" cy="60" r="46" /></clipPath>
          <circle cx="60" cy="60" r="46" fill="currentColor" opacity="0.1" />
          <g clipPath={`url(#mm-calm-clip-${uid})`}>
            <path className="mm-illus-wave" d="M-30 72 q 15 -10 30 0 t 30 0 t 30 0 t 30 0 t 30 0 V120 H-30 Z" fill="currentColor" opacity="0.28" />
            <path className="mm-illus-wave mm-illus-wave--2" d="M-30 84 q 15 -9 30 0 t 30 0 t 30 0 t 30 0 t 30 0 V120 H-30 Z" fill="currentColor" opacity="0.45" />
          </g>
          <g className="mm-illus-leaf" style={{ transformOrigin: '60px 40px' }}>
            <path d="M60 30 C 72 34, 72 50, 60 54 C 48 50, 48 34, 60 30 Z" fill="currentColor" opacity="0.7" />
            <path d="M60 32 V52" stroke="#fff" strokeOpacity="0.6" strokeWidth="1.4" />
          </g>
        </svg>
      );
    case 'focused':
      return (
        <svg viewBox="0 0 120 120" className="mm-illus__svg">
          {[40, 28, 16].map((r, i) => (
            <circle key={r} className="mm-illus-target" style={{ transformOrigin: '60px 60px', animationDelay: `${i * 0.4}s` }}
              cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="3" opacity={0.35 + i * 0.2} />
          ))}
          <circle cx="60" cy="60" r="5" fill="currentColor" />
          {[[60, 12], [60, 108], [12, 60], [108, 60]].map(([x, y], i) => (
            <rect key={i} x={x - 1} y={y - 5} width="2" height="10" rx="1" fill="currentColor" opacity="0.6"
              transform={x === 60 ? '' : `rotate(90 ${x} ${y})`} />
          ))}
        </svg>
      );
    case 'sad':
      return (
        <svg viewBox="0 0 120 120" className="mm-illus__svg">
          <g className="mm-illus-cloud" style={{ transformOrigin: '60px 46px' }}>
            <circle cx="46" cy="48" r="16" fill="currentColor" opacity="0.55" />
            <circle cx="66" cy="42" r="20" fill="currentColor" opacity="0.6" />
            <circle cx="82" cy="50" r="15" fill="currentColor" opacity="0.5" />
            <rect x="44" y="50" width="42" height="16" rx="8" fill="currentColor" opacity="0.6" />
          </g>
          {[[50, 0], [64, 0.5], [78, 1]].map(([x, delay], i) => (
            <circle key={i} className="mm-illus-rain" style={{ animationDelay: `${delay}s` }}
              cx={x} cy="74" r="3" fill="currentColor" opacity="0.6" />
          ))}
        </svg>
      );
    case 'anxious':
      return (
        <svg viewBox="0 0 120 120" className="mm-illus__svg">
          {[44, 32, 20].map((r, i) => (
            <circle key={r} className="mm-illus-anx" style={{ transformOrigin: '60px 60px', animationDelay: `${i * 0.5}s` }}
              cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.4" />
          ))}
          <circle className="mm-illus-anx-core" cx="60" cy="60" r="9" fill="currentColor" style={{ transformOrigin: '60px 60px' }} />
        </svg>
      );
    case 'tired':
    default:
      return (
        <svg viewBox="0 0 120 120" className="mm-illus__svg">
          <g className="mm-illus-moon" style={{ transformOrigin: '58px 60px' }}>
            <path d="M 58 24 A 34 34 0 1 0 58 96 A 26 26 0 1 1 58 24 Z" fill="currentColor" opacity="0.85" />
          </g>
          {[[92, 30, 0], [100, 58, 0.7], [84, 74, 1.3], [30, 40, 0.4]].map(([x, y, delay], i) => (
            <path key={i} className="mm-illus-star" style={{ transformOrigin: `${x}px ${y}px`, animationDelay: `${delay}s` }}
              d={`M${x} ${y - 4} L${x + 1.1} ${y - 1.1} L${x + 4} ${y} L${x + 1.1} ${y + 1.1} L${x} ${y + 4} L${x - 1.1} ${y + 1.1} L${x - 4} ${y} L${x - 1.1} ${y - 1.1} Z`}
              fill="currentColor" opacity="0.8" />
          ))}
        </svg>
      );
  }
};

export const AnimatedMoodIllustration: React.FC<{
  mood: MoodId;
  size?: IllustrationSize;
  accent?: string;
  className?: string;
}> = ({ mood, size = 'md', accent, className = '' }) => {
  const px = SIZE_PX[size];
  const uid = useRef<string>('');
  if (!uid.current) uid.current = String(++instanceSeq);
  return (
    <div
      className={`mm-illus mm-illus--${size} mm-illus--${mood} ${className}`}
      style={{ width: px, height: px, color: accent || 'var(--mood-accent, #3fb9a0)' }}
      aria-hidden="true"
    >
      <Scene mood={mood} uid={uid.current} />
    </div>
  );
};

export default AnimatedMoodIllustration;
